import React, { useEffect, useState } from 'react';
import { funds as fundsApi } from '../services/api';
import { HeartHandshake, Sparkles } from 'lucide-react';

/**
 * Live fundraiser thermometer. Reads from GET /api/funds/live (points-based ledger
 * for auction + Deal Bin sales) and renders a 50/50 community-vs-youth split bar.
 * Auto-refreshes every 60s.
 */
export default function FundraiserThermometer({ goalPts = 100000, compact = false }) {
  const [data, setData] = useState({ totalPts: 0, communityPts: 0, youthPts: 0 });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const d = await fundsApi.live(); if (alive) setData(d); } catch (e) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const totalPts = data.totalPts || 0;
  const pctToGoal = Math.min(100, (totalPts / goalPts) * 100);
  const half = totalPts / 2;

  return (
    <div data-testid="fundraiser-thermometer" className={`pbb-card ${compact ? 'p-4' : 'p-5 sm:p-6'} bg-gradient-to-br from-green-500/10 via-transparent to-emerald-500/5 border-green-500/25`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
            <HeartHandshake size={16} /> Community & Youth Fund
            <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-300 uppercase tracking-wider">
              <Sparkles size={9} /> Live
            </span>
          </div>
          <div className="mt-1 text-2xl sm:text-3xl font-bold">
            {totalPts.toLocaleString()} <span className="text-sm text-gray-400 font-normal">pts raised</span>
          </div>
          <div className="text-[11px] text-gray-500">Split evenly across both nonprofits · 100% of auction & Deal Bin proceeds</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Goal</div>
          <div className="text-lg font-semibold text-green-300">{goalPts.toLocaleString()} pts</div>
          <div className="text-[10px] text-gray-500">{pctToGoal.toFixed(1)}%</div>
        </div>
      </div>

      {/* 50/50 thermometer */}
      <div className="mt-4 relative">
        <div className="h-4 rounded-full bg-[#0f0f0f] border border-[#1a1a1a] overflow-hidden relative">
          <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all" style={{ width: `${pctToGoal}%` }} />
          {/* vertical center line marking the 50/50 midpoint of the goal */}
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-black/40" />
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Community Fund</div>
            <div className="text-base font-semibold text-green-400">{Math.floor(data.communityPts || 0).toLocaleString()} pts</div>
          </div>
          <div className="rounded-lg bg-[#141f3e] border border-[#233b6f] px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-400">Youth Fund</div>
            <div className="text-base font-semibold text-blue-300">{Math.floor(data.youthPts || 0).toLocaleString()} pts</div>
          </div>
        </div>
      </div>
    </div>
  );
}
